import React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { TYPOGRAPHY_SIZE } from 'mm-theme-configuration/dist/consts';
import { HeaderLinksDataProps } from '../Header';
import { notActiveLinkIdleColor } from '../header.utils';
import { useTheme } from '../../../../../theming/useTheme';
import { MoreLinks } from './MoreLinks';
import { generateLinkStyleFromTheme, TextLinkComponent } from '../../../../partials/link/TextLink';
import { MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';
import { Span, semanticCSS } from '../../../../../typography/semanticTags';
import {
  FontSizesStyles,
  getStyleBySize,
  SemanticTagThemeProps,
} from '../../../../../typography/semanticTags/semanticTags.utils';
import { BaseUnit } from '../../../../../theming/baseUnitDefinition';

const componentConfigThemeKey = 'secondaryLinks';

export interface SecondaryLinksThemeProps extends SemanticTagThemeProps {
  baseUnit: BaseUnit
  hoverOtherLinksColors: string
  idleColor: string
  activeStateColor: string
  hoverStateColor: string
}

interface state {
  isHoveredLink: boolean
}

interface SecondaryLinksLogicProps {
  maxVisibleLinks?: number
}

interface SecondaryLinksDataProps {
  moreLinksText: string
  links: HeaderLinksDataProps[]
  pagePath: string
}

export type SecondaryLinksProps = SecondaryLinksDataProps & SecondaryLinksLogicProps;

export type SecondaryLinksComponentProps = SecondaryLinksProps & SecondaryLinksThemeProps;

const getTextStyle = (baseUnit: BaseUnit, isLastLink: boolean) => {
  return StyleSheet.create({
    link: {
      width: 'max-content',
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        paddingRight: isLastLink ? `${baseUnit * 2}px` : 0,
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        paddingRight: isLastLink ? `${baseUnit * 2}px` : 0,
      },
    },
  });
};

const getContainerStyle = (baseUnit: BaseUnit) => {
  return StyleSheet.create({
    container: {
      display: 'grid',
      gridAutoFlow: 'column',
      alignItems: 'center',
      height: '100%',
      gridColumnGap: `${baseUnit}px`,
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        overflowX: 'auto',
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        overflowX: 'auto',
      },
    },
    dropDownMenuWrapper: {
      height: '100%',
      [MEDIA_BREAKPOINTS.LARGE]: {
        display: 'block',
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        display: 'none',
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        display: 'none',
      },
    },
  });
};
export const isLastLink = (index: number, linksLength: number) => index + 1 === linksLength;

const getContentStyle = (isActiveLink: boolean, fontSizesStyles: FontSizesStyles) => {
  const fontSizesActive = { fontSizeSmall: TYPOGRAPHY_SIZE.BIGGER, fontSizeMedium: TYPOGRAPHY_SIZE.BIGGER, fontSizeLarge: TYPOGRAPHY_SIZE.BIGGER };
  const fontSizesNotActive = { fontSizeSmall: TYPOGRAPHY_SIZE.NORMAL, fontSizeMedium: TYPOGRAPHY_SIZE.NORMAL, fontSizeLarge: TYPOGRAPHY_SIZE.NORMAL };
  return isActiveLink ? getStyleBySize(fontSizesStyles, fontSizesActive) : getStyleBySize(fontSizesStyles, fontSizesNotActive);
};

export const SecondaryLinksComponent: React.FunctionComponent<SecondaryLinksComponentProps> = props => {
  const createLink = ({ idleColor, activeStateColor, hoverStateColor, baseUnit, hoverOtherLinksColors, fontSizesStyles }: SecondaryLinksComponentProps, linksLength: number, isHoveredLink: boolean, pagePath: string) => (
    (linkData: HeaderLinksDataProps, index: number) => {
      const isActiveLink = linkData.href === pagePath;
      const { maxVisibleLinks = 6 } = props;
      const calculatedIdleColor = isActiveLink ? activeStateColor : notActiveLinkIdleColor(isHoveredLink, hoverOtherLinksColors, idleColor);
      const calculatedHoverColor = isActiveLink ? activeStateColor : hoverStateColor;

      const linkStyleBasedOnScreen = linksLength > maxVisibleLinks && index >= maxVisibleLinks - 1
        ? {
          [MEDIA_BREAKPOINTS.LARGE]: {
            display: 'none',
          },
          [MEDIA_BREAKPOINTS.SMALL]: {
            display: 'inline-flex',
          },
          [MEDIA_BREAKPOINTS.MEDIUM]: {
            display: 'inline-flex',
          },
        }
        : {};

      const linkStyle = generateLinkStyleFromTheme({ idleColor: calculatedIdleColor, activeStateColor, hoverStateColor: calculatedHoverColor }, linkStyleBasedOnScreen);
      const style = getTextStyle(baseUnit, isLastLink(index, linksLength));
      const contentStyle = getContentStyle(isActiveLink, fontSizesStyles);

      return (
        <TextLinkComponent key={index} href={linkData.href} linkStyle={linkStyle}>
          <Span styles={semanticCSS(contentStyle, style.link)}>{linkData.text}</Span>
        </TextLinkComponent>
      );
    }
  );

  const { links, baseUnit, moreLinksText, maxVisibleLinks = 6, pagePath } = props;
  const [isHoveredLink, setIsHoveredLink] = React.useState(false);
  const style = getContainerStyle(baseUnit);
  const moreLinks = links.length > maxVisibleLinks ? links.slice(maxVisibleLinks - 1) : [];

  return (
    <div className={css(style.container)}>
      <div className={css(style.container)} onMouseEnter={() => setIsHoveredLink(true)} onMouseLeave={() => setIsHoveredLink(false)}>
        {links.map(createLink(props, links.length, isHoveredLink, pagePath))}
      </div>
      <div className={css(style.dropDownMenuWrapper)}>
        {moreLinks.length > 0 && <MoreLinks moreLinksText={moreLinksText} links={moreLinks} pagePath={pagePath} />}
      </div>
    </div>
  );
};

const mapThemeProps = (theme: SecondaryLinksThemeProps) => {
  return {
    idleColor: theme.idleColor,
    activeStateColor: theme.activeStateColor,
    hoverStateColor: theme.hoverStateColor,
    baseUnit: theme.baseUnit,
    hoverOtherLinksColors: theme.hoverOtherLinksColors,
    fontSizesStyles: theme.fontSizesStyles,
  };
};

export const SecondaryLinks = (props: SecondaryLinksProps) => {
  const themeContext = useTheme(mapThemeProps, componentConfigThemeKey);
  return <SecondaryLinksComponent {...themeContext} {...props} />;
};
